 /*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

 import IScene from "../scene_interface";
 import {GlobeModel, GlobeWorkerManager, RenderDocument, Renderer} from "js_renderer";
 import IModel from "../structural_elements/model_interface";
  import ChunkLODNode from "../elements/chunk_lod_node";
 import Factory from "../elements/factory";
 import IGlobe from "./globe_interface";
 import {getIndexToFlattenedTree3} from "./common";

 export enum DebugNodeStateColor {
     "State-0" = "white",        //CREATED = 0,
     "State-1" = "yellow",       //REQUESTED = 1,
     "State-2" = "red",          //FAILED = 2,
     "State-3" = "black",        //DATAREADY = 3,
     "State-4" = "blue",         //COMMITTEDTOGPU = 4,
     "State-5" = "lime"          //SETTLEDONGPU = 5
 }

export default
class Globe implements IGlobe {

    private _sceneAPI: IScene;
    private _renderDocument: RenderDocument;
    private _renderer: Renderer;
    private _globeModel: IModel;
    private _globeGroupUID: number;
    private _globeModelElement: GlobeModel;
    private _globeWorkerManager: GlobeWorkerManager;
    private _swissGridPatches: any;
    private _postponeRendererUpdateNofFrames: number;
    private _timeReservedForGPUDataUpdatePct: number;
    private _ticTack: boolean;
    private _singleTileDebug: boolean;
    private _debug: any;
    private _debugEnabled: boolean;
    private _updatesLimitedPerFrame: boolean = false;

    constructor(aRenderDocument: RenderDocument) {
        this._renderDocument = aRenderDocument;
        this._sceneAPI = aRenderDocument.getSceneAPI();
        this._renderer = this._renderDocument.getRenderer();

        this._globeModel = this._sceneAPI.getDocument().createModel("Globe model", null, null, false, false);
        this._globeModel.disableManipulation();
        this._globeModel.setSelectionAllowed(false);

        this._globeGroupUID = this._globeModel.addGroup(null, "Globe", true);

        this._globeModelElement = null;

        this._globeWorkerManager = new GlobeWorkerManager(this._sceneAPI, this);

        this._swissGridPatches = {};

        this._postponeRendererUpdateNofFrames = 0;
        this._timeReservedForGPUDataUpdatePct = 10;
        this._ticTack = true;

        this._singleTileDebug = true;

        if (this._singleTileDebug) {
            this._debug = {
                treeStructure: {
                    enabled: false,
                    left: "0px",
                    top: "50px",
                    canvas: null
                },
                nodeState: {
                    enabled: false,
                    left: "266px",
                    top: "50px",
                    canvas: null
                },
                requiredLevel: {
                    enabled: false,
                    left: "0px",
                    top: "316px",
                    canvas: null
                },
                currentLevel: {
                    enabled: false,
                    left: "266px",
                    top: "316px",
                    canvas: null
                },
                visibilityState: {
                    enabled: false,
                    left: "0px",
                    top: "582px",
                    canvas: null
                }
            };
        } else {
            let whatToDebug = "nodeState";
            this._debug = {};
            let ranges = [{name: "e5", left: 0, top: 50}, {name: "e6", left: 266, top: 50}, {
                name: "f5",
                left: 0,
                top: 316
            }, {name: "f6", left: 266, top: 316}];
            for (let i = 0; i < ranges.length; i++) {
                this._debug[whatToDebug + "_" + ranges[i].name] = {
                    enabled: false,
                    left: ranges[i].left + "px",
                    top: ranges[i].top + "px",
                    canvas: null
                };
            }
        }

        this._debugEnabled = false;
        this.enableDebug(false);

        this.setUpdatesLimitedPerFrame(false);
    }

    public setUpdatesLimitedPerFrame(aValue: boolean) {
        this._updatesLimitedPerFrame = aValue;
        this._renderer.getTexturesManager().setLimitGpuUploadsPerFrame(aValue);
        this._globeWorkerManager.setUpdatesLimited(aValue);
    }

    public getDebugEnabled(): boolean {
        return this._debugEnabled;
    }

    public enableDebug(aValue: boolean) {
        if (this._debugEnabled !== aValue) {
            let keys = Object.keys(this._debug);
            for (let i = 0; i < keys.length; i++) {
                this._enableDebug(aValue, keys[i]);
            }
        }

        this._globeWorkerManager.enableDebug(aValue);

        this._debugEnabled = aValue;
    }

    public updateDebugData(aUpdateMessage: any) {
        for (let key in aUpdateMessage.data.values) {
            let target;
            if (this._singleTileDebug) {
                target = this._debug[key];
            } else {
                target = this._debug[key + "_" + aUpdateMessage.patchInfo.name];
            }

            if (target === undefined) {
                continue;
            }

            if (!target.canvas) {
                continue;
            }

            let values = aUpdateMessage.data.values[key];
            let ctx = target.canvas.getContext("2d");
            ctx.clearRect(0, 0, target.canvas.width, target.canvas.height);

            for (let level = 0; level < aUpdateMessage.patchInfo.levels; level++) {
                let levelSize = Math.pow(2, level);

                let squareSize = Math.pow(2, aUpdateMessage.patchInfo.levels - level - 1) * 8;

                for (let row = 0; row < levelSize; row++) {
                    for (let col = 0; col < levelSize; col++) {
                        let state = values[getIndexToFlattenedTree3(level, 0, row, col, aUpdateMessage.patchInfo.nc)];
                        if (state > 0) {
                            let color = DebugNodeStateColor["State-" + state];
                            let centerX = col * squareSize + squareSize / 2.0;
                            let centerY = row * squareSize + squareSize / 2.0;

                            ctx.beginPath();
                            ctx.lineWidth = "1";
                            ctx.strokeStyle = color;
                            ctx.rect(
                                centerX - (squareSize / 2) + level * 0.1 * 8,
                                centerY - (squareSize / 2) + level * 0.1 * 8,
                                squareSize - (level * 0.1) * 8,
                                squareSize - (level * 0.1) * 8
                            );
                            ctx.stroke();
                        }
                    }
                }
            }
        }
    }

    private _enableDebug(aEnabled: boolean, aId: string) {
        this._debug[aId].enabled = aEnabled;

        if (aEnabled) {
            if (this._debug[aId].canvas) {
                this._debug[aId].canvas.remove();
            }

            this._debug[aId].canvas = this._createCanvasForDebugOutput(aId, 8, this._debug[aId].left, this._debug[aId].top);
        } else if (!aEnabled) {
            if (this._debug[aId].canvas) {
                this._debug[aId].canvas.remove();
            }
        }
    }

    private _createCanvasForDebugOutput(aId, aZIndex, aOffsetX, aOffsetY) {
        let canvas = document.createElement('canvas');
        canvas.id = aId;
        canvas.width = 256;
        canvas.height = 256;
        canvas.style.zIndex = aZIndex;
        canvas.style.position = "absolute";
        canvas.style.border = "1px solid";
        canvas.style.left = aOffsetX;
        canvas.style.top = aOffsetY;

        document.body.appendChild(canvas);

        return canvas;
    }

    public setGlobeModelElement(aGlobeModelElement) {
        this._globeModelElement = aGlobeModelElement;
        this._globeWorkerManager.startGlobeRendering(
            aGlobeModelElement.getModel().getBaseUrl(),
            this._globeModelElement.getPatches(),
            this._globeModelElement.getModels(),
            this._globeModelElement._modelDescription.zeropointSettings.zeropoint.getTranslationMat()
        );
    }

    public viewpointChanged() {
        this._globeWorkerManager.viewpointChanged();
    }

    public render(aRenderer: Renderer) {
        this._ticTack = !this._ticTack;

        if (this._postponeRendererUpdateNofFrames > 0 || this._ticTack) {
            this._postponeRendererUpdateNofFrames--;
            this._sceneAPI.viewModeModule.scheduleRedraw();
            if (this._postponeRendererUpdateNofFrames < 0) {
                this._postponeRendererUpdateNofFrames = 0;
            }
            return;
        }

        let cameraPosition = this._sceneAPI.viewModeModule.getCameraPosition();

        //TODO: we should change it dymaic when in fly/animation mode we keep it more restricting whereas in examiner mode we do not restrict it al all
        this._timeReservedForGPUDataUpdatePct = aRenderer.currentFrameTime * 0.1; // 10% of frame time is reserved to make planet updates
        let sceneUpdatesNow = performance.now();
        let sceneUpdatesEnd = sceneUpdatesNow + this._timeReservedForGPUDataUpdatePct;
        let key = null;
        for (let name in this._swissGridPatches) {
            let patch = this._swissGridPatches[name];
            let keysToRemove = [];

            // sort the commits
            patch.updatesList.sort(function (a, b) {
                return b.priority - a.priority; // the priority comes from sycnhronization manager and is computed from priority of each tile based on its usage count
            });

            //for (let key in patch.updates) {
            for (let k = 0; k < patch.updatesList.length; k++) {
                this.commit2gpu(patch.updatesList[k]);
                keysToRemove.push(patch.updatesList[k].uid);

                sceneUpdatesNow = performance.now();

                this._sceneAPI.viewModeModule.scheduleRedraw();

                if (this._updatesLimitedPerFrame && sceneUpdatesNow > sceneUpdatesEnd) {
                    this._sceneAPI.viewModeModule.scheduleRedraw();
                    this._postponeRendererUpdateNofFrames = 1;
                    break;
                }
            }

            for (let i = 0; i < keysToRemove.length; i++) {
                // search for updates in the list
                for (let j = 0; j < patch.updatesList.length; j++) {
                    if (patch.updatesList[j].uid === keysToRemove[i]) {
                        patch.updatesList.splice(j, 1);
                    }
                }
                delete patch.updates[keysToRemove[i]];
            }

            let stillWaitingQueries = {};
            let query = null;
            for (let key in patch.waitingForGpu) {
                query = patch.waitingForGpu[key];
                if (!this.checkIsOnGpu(query)) {
                    stillWaitingQueries[key] = query;
                }
            }
            patch.waitingForGpu = stillWaitingQueries;
        }
    }

    public getOrCreateSwissGridPatch(aPatchInfo: any) {
        let swissGridPatch = this._swissGridPatches[aPatchInfo.name];
        if (swissGridPatch === undefined) {
            swissGridPatch = {
                visibility: null,
                nodes: {},
                updates: {},
                updatesList: [],
                waitingForGpu: {}
            };
            this._swissGridPatches[aPatchInfo.name] = swissGridPatch;
        }

        return swissGridPatch;
    }

    public updateVisibility(aData: any) {
        let swissGridPatch = this.getOrCreateSwissGridPatch(aData.patchInfo);
        swissGridPatch.visibility = aData.visibility;

        // HACK for now until we will have full patch otherwise we will still see the coarsest level 0
        /*for (let i = 1; i < swissGridPatch.visibility.length; i++) {
            if (swissGridPatch.visibility[i] === 1) {
                swissGridPatch.visibility[0] = 0;
                break;
            }
        }*/

        this._sceneAPI.viewModeModule.scheduleRedraw();
    }

    public getVisibility(aRange: any, aLayer: number, aRow: number, aCol: number, aLevel: number, aNc: number) {
        let swissGridPatch = this.getOrCreateSwissGridPatch(aRange);
        return swissGridPatch.visibility[getIndexToFlattenedTree3(aLevel, aLayer, aRow, aCol, aNc)];
    }

    public scheduleCommit2gpu(aData: any) {
        let swissGridPatch = this.getOrCreateSwissGridPatch(aData.patchInfo);
        if (swissGridPatch.updates[aData.uid] === undefined) {
            swissGridPatch.updatesList.push(aData);
            swissGridPatch.updates[aData.uid] = aData;
        }
    }

    public checkIsOnGpu(query: any) {
        if (query.node._renderDocumentProperties.renderElement.getGeometryObjects()[0].getIsReadyToRender()) {
            this._globeWorkerManager.settledOnGPU(query.data);

            return true;
        }

        return false;
    }

    public commit2gpu(aData) {
        let chunkLodNode: any = Factory.createAPIElement(this._renderDocument, ChunkLODNode.TypeName);
        this._sceneAPI.getDocument().attachElementToInternalModel(chunkLodNode);
        chunkLodNode.setData(aData);

        let swissGridPatch = this.getOrCreateSwissGridPatch(aData.patchInfo);
        swissGridPatch.nodes[aData.uid] = chunkLodNode;

        chunkLodNode.setPatch(swissGridPatch);

        this._globeModel.addElement(chunkLodNode, this._globeGroupUID);

        swissGridPatch.waitingForGpu[aData.uid] = {data: aData, node: chunkLodNode};
    }

    public removeFromGpu(aData: any) {
        let swissGridPatch = this.getOrCreateSwissGridPatch(aData.patchInfo);
        let chunkLodNode = swissGridPatch.nodes[aData.uid];
        if (chunkLodNode !== undefined) {
            this._globeModel.removeElement(chunkLodNode);
            delete swissGridPatch.nodes[aData.uid];
        }

        // look to scheduled commits
        if (swissGridPatch.updates[aData.uid] !== undefined) {
            // search for updates in the list
            for (let i = 0; i < swissGridPatch.updatesList.length; i++) {
                if (swissGridPatch.updatesList[i].uid === aData.uid) {
                    swissGridPatch.updatesList.splice(i, 1);
                }
            }
            delete swissGridPatch.updates[aData.uid];
        }

        if (swissGridPatch.waitingForGpu[aData.uid] !== undefined) {
            delete swissGridPatch.waitingForGpu[aData.uid];
        }
    }
}